import {
  AgentDiscoveryStageId,
  AgentDiscoveryStageProgress,
  DiscoveredProject,
} from "@/api/temp/AgentEstateService";
import { type GeneratedAgent } from "./generate";
import { type StageWindow, stageRatio, stageStatus } from "./phases";

/**
 * The wizard's lightweight discovery, derived from the clock the same way the
 * onboarding run is (see `phases.ts`): nothing ticks, every read computes the
 * same snapshot from `startedAt`.
 *
 * Short on purpose - it only lists the registry's projects so the
 * administrator can scope onboarding. The import and the deep analysis run in
 * the sync job after onboarding starts.
 */
const DISCOVERY_PREVIEW_MS = 2_500;

const STAGE_WINDOWS: ReadonlyArray<StageWindow<AgentDiscoveryStageId>> = [
  { id: "CONNECTING", start: 0, end: 0.3 },
  { id: "LISTING_PROJECTS", start: 0.2, end: 1 },
];

export const discoveryComplete = (elapsedMs: number): boolean =>
  elapsedMs >= DISCOVERY_PREVIEW_MS;

export function discoveryStages(
  elapsedMs: number,
  projectCount: number,
): AgentDiscoveryStageProgress[] {
  return STAGE_WINDOWS.map(window => {
    const ratio = stageRatio(window, elapsedMs, DISCOVERY_PREVIEW_MS);
    // CONNECTING is a single step; LISTING_PROJECTS counts what it has read.
    const total = window.id === "CONNECTING" ? 1 : projectCount;
    return {
      id: window.id,
      status: stageStatus(ratio),
      value: Math.round(total * ratio),
      total,
    };
  });
}

/**
 * A project's id, derived from its name. Stable across runs, so a project the
 * administrator selected keeps its identity when the same connection is read
 * again.
 */
export const projectIdOf = (project: string): string =>
  `project-${project
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;

/** The provider's projects, in first-seen order, with their agent counts. */
export function discoveredProjectsOf(
  agents: GeneratedAgent[],
): DiscoveredProject[] {
  const projects = new Map<string, DiscoveredProject>();
  agents.forEach(agent => {
    const id = projectIdOf(agent.project);
    const project = projects.get(id) ?? {
      id,
      name: agent.project,
      agentCount: 0,
    };
    project.agentCount = (project.agentCount ?? 0) + 1;
    projects.set(id, project);
  });
  return [...projects.values()];
}

/**
 * The agents onboarding imports: every agent in the selected projects, in
 * discovery order, re-indexed so the imported estate's positional cutoffs
 * (discovered, classified) count the imported agents rather than slots left
 * behind by excluded projects.
 */
export function agentsInProjects(
  agents: GeneratedAgent[],
  projectIds?: readonly string[],
): GeneratedAgent[] {
  if (!projectIds) return agents;
  const selected = new Set(projectIds);
  return importAgents(
    agents,
    agents
      .filter(agent => selected.has(projectIdOf(agent.project)))
      .map(agent => agent.id),
  );
}

/**
 * The imported subset by id, re-indexed. Sessions persist imported ids, so a
 * restored environment rebuilds exactly the agents its projects contained.
 */
export function importAgents(
  agents: GeneratedAgent[],
  agentIds?: readonly string[],
): GeneratedAgent[] {
  if (!agentIds) return agents;
  const selected = new Set(agentIds);
  return agents
    .filter(agent => selected.has(agent.id))
    .map((agent, sequenceIndex) => ({ ...agent, sequenceIndex }));
}
